import { PayloadAction } from "@reduxjs/toolkit";
import { put, select, take, takeLatest, takeLeading } from "redux-saga/effects";

import i18n from "@/i18n";
import { getBatteries, getBatterySizerSettings, getHasFetchedInitialBatteryRecommendations } from "@/lib/selectors";
import { OptimizeDesignAction } from "@/types";

import { closeConfirmationSaga, confirmationSaga } from "../dialogs";
import { destroyNotification, sendNotification } from "../notifications/notifications.slice";
import { startSubmitOptimizationSaga, submitOptimization } from "../proposal";
import { fetchBatterySizerRecommendations } from "./battery-sizer.actions";
import batterySizerSlice from "./battery-sizer.slice";
import dialogsSlice from "../dialogs/dialogs.slice";

const selectBatteryNotificationId = "select-recommended-battery";

function* handleFetchedInitialRecommendationsSaga() {
  const hasFetchedInitial = yield select(state => getHasFetchedInitialBatteryRecommendations(state));

  if (!hasFetchedInitial) {
    yield put(batterySizerSlice.actions.setHasFetchedInitial(true));
  }
}

function* submitSelectedBatterySaga(batteries: { id: string; count: number }[]) {
  const { attachment, defermentPercentage, hours } = yield select(state => getBatterySizerSettings(state));
  yield put(dialogsSlice.actions.setDialogIsLoading(true));

  yield put(
    startSubmitOptimizationSaga({
      type: "batteries",
      data: {
        batteries,
        batteryDetails: {
          purpose: attachment,
          defermentPercentage,
          hours,
        },
      },
    } as OptimizeDesignAction<"batteries">)
  );

  const nextAction = yield take([submitOptimization.fulfilled, submitOptimization.rejected]);

  yield put(dialogsSlice.actions.setDialogIsLoading(false));

  if (nextAction.type === submitOptimization.rejected.type) {
    yield put(
      sendNotification({
        id: selectBatteryNotificationId,
        type: "error",
        title: i18n.t("battery-sizer.error-selecting-battery-message"),
        body: nextAction.payload?.["errors"] || nextAction.error.message,
      })
    );
  }
}

function* selectBatteryRecommendationSaga(action: PayloadAction<{ id: string; count: number }[]>) {
  const currentBatteries = yield select(state => getBatteries(state));

  // Close the error notification if open
  yield put(destroyNotification(selectBatteryNotificationId));

  if (!currentBatteries.length) {
    yield submitSelectedBatterySaga(action.payload);
    return;
  }

  // If there are currently selected batteries, show confirmation that existing batteries will be replaced by this selection
  const confirmed = yield confirmationSaga({
    headerText: i18n.t("battery-sizer.selected-battery-override-confirm.title"),
    bodyText: i18n.t("battery-sizer.selected-battery-override-confirm.description"),
    confirmButtonText: i18n.t("battery-sizer.selected-battery-override-confirm.confirmText"),
  });

  if (confirmed) {
    yield submitSelectedBatterySaga(action.payload);
  }

  yield closeConfirmationSaga();
}

export function* batterySizerSaga() {
  yield takeLeading(fetchBatterySizerRecommendations.fulfilled, handleFetchedInitialRecommendationsSaga);
  yield takeLatest(batterySizerSlice.actions.selectBatteryRecommendation, selectBatteryRecommendationSaga);
}
